import RPilotChat from './components/RPilotChat.vue'
import { RPILOT_CONFIG } from './composables/useRPilotConfig.js'
import { useBridgeStore } from './stores/bridge.js'
import { setApiConfig } from './api/client.js'
import { buildInfo } from './version.js'

const DEFAULTS = {
  apiBaseUrl: '/api',
  scopedToken: null,
  apiUrl: null,
  projectCode: null,
  userId: null,
  // The host UI's locale (e.g. "en", "nl"), used to localize widget labels.
  // Null falls back to English. Display-only: the agent's reply language is
  // driven server-side from the RDrive user record, not by this value.
  locale: null,
  // Session id to resume on mount — an iframe host passes the id it captured
  // from the widget's sessionChanged bridge action back via ?sessionId= when
  // it recreates the iframe (close → reopen). Adopted once at mount; an id
  // that no longer resolves falls back to a fresh chat.
  initialSessionId: null,
  // Host-supplied context describing what the user is working on (selected
  // record, mode, etc.). Sent in the POST /sessions body so the backend agent
  // can prime its system prompt.
  sessionContext: null,
  sessionCreateOptions: { agentCode: 'rpilot-ai-spa' },
  namespace: 'rpilot',
  credentials: 'include',
  headers: null,
  // URL the standalone `rdrive_pick_location` tool loads in its iframe. Must
  // serve the rdrive web build that exposes window.snagr.planViewer. Falls
  // back to a same-origin path that the standalone deployment is expected to
  // mount. Vue-to-Vue consumers that don't use the picker can ignore this.
  rdriveWebViewUrl: '/rdrive-web-view/index.html#/plan-viewer',
  // Opt-in postMessage bridge for iframe deployments. Leave null for Vue-to-Vue
  // embeddings. Shape: { enabled, allowedOrigins, parentOrigin, hostSource,
  // clientSource, navigateAction, readyAction, setModeAction,
  // sessionChangedAction, refreshTokenAction, interceptLinks, allowLocalhost }.
  // See src/composables/useParentBridge.js.
  bridge: null,
}

export default {
  install(app, options = {}) {
    const config = { ...DEFAULTS, ...options }
    app.provide(RPILOT_CONFIG, config)

    // Seed the API client module so non-component callers (stores, composables)
    // see the apiBaseUrl + headers without going through Vue's inject() chain.
    setApiConfig({
      apiBaseUrl: config.apiBaseUrl,
      credentials: config.credentials,
      headers: config.headers,
    })

    // Seed the bridge store from plugin config. The bridge store carries the
    // RPilot-MCP auth values (scopedToken/apiUrl/projectCode/userId) that flow
    // through every session-related backend call.
    const bridge = useBridgeStore()
    bridge.init({
      scopedToken: typeof config.scopedToken === 'function' ? config.scopedToken() : config.scopedToken,
      apiUrl: config.apiUrl,
      projectCode: config.projectCode,
      userId: config.userId,
      agentCode: config.sessionCreateOptions?.agentCode ?? null,
      sessionContext: config.sessionContext,
    })

    // Build stamp for support ("which build is this iframe running?"). Also
    // shown in the settings drawer and sent with the `rpilot.ready` event.
    if (typeof window !== 'undefined') window.__RPILOT_VERSION__ = buildInfo.version

    app.component('RPilotChat', RPilotChat)
  },
}

export { RPilotChat, buildInfo }
